import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CalendarIcon, Clock, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClientBookingsCalendar = ({ bookings, onUpdate }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  
  // Group bookings by date
  const bookingsByDate = bookings.reduce((acc, booking) => {
    const date = booking.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(booking);
    return acc;
  }, {});

  // Get upcoming bookings (next 7 days)
  const getUpcomingBookings = () => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate >= today && bookingDate <= nextWeek;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay())); // End at Saturday
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      days.push(new Date(date));
    }
    
    return days;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDate(date) === formatDate(today);
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === selectedDate.getMonth();
  };

  const getBookingsForDate = (date) => {
    const dateStr = formatDate(date);
    return bookingsByDate[dateStr] || [];
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, null, {
        params: { status }
      });
      toast.success('Booking updated');
      onUpdate();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const nextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };

  return (
    <div className="space-y-6">
      {/* Header with view toggle */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('myBookings')}</h2>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="list">📋 List View</TabsTrigger>
            <TabsTrigger value="calendar">📅 Calendar View</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Tabs value={viewMode}>
        {/* List View */}
        <TabsContent value="list" className="space-y-6">
          {/* Upcoming Bookings Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Upcoming Appointments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getUpcomingBookings().length === 0 ? (
                <p className="text-gray-500 text-center py-4">No upcoming appointments</p>
              ) : (
                <div className="space-y-3">
                  {getUpcomingBookings().slice(0, 3).map(booking => (
                    <div key={booking.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(booking.status)}`}></div>
                      <div className="flex-1">
                        <p className="font-medium">{booking.specialist_name} - {booking.service_category}</p>
                        <p className="text-sm text-gray-600">{booking.date} at {booking.time_slot}</p>
                      </div>
                      <Badge variant={booking.status === 'confirmed' ? 'default' : 'secondary'}>
                        {booking.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Bookings List */}
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <Card>
                <CardContent className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg mb-2">No bookings yet</p>
                  <p className="text-gray-400 text-sm">Book your first service to get started!</p>
                  <Button className="mt-4" onClick={() => window.location.href = '/services'}>
                    Browse Services
                  </Button>
                </CardContent>
              </Card>
            ) : (
              bookings.map(booking => (
                <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{booking.specialist_name}</h3>
                          <Badge variant="outline" className="capitalize">
                            {booking.service_category}
                          </Badge>
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{booking.date} at {booking.time_slot}</span>
                            <span className="text-gray-400">({booking.duration_hours}h)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{booking.address}</span>
                          </div>
                          {booking.special_instructions && (
                            <div className="flex items-start gap-2">
                              <span className="w-4 h-4 text-center">📝</span>
                              <span>{booking.special_instructions}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold text-lg text-green-600">
                          €{booking.total_amount}
                        </p>
                        <Badge 
                          variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                          className={`${getStatusColor(booking.status)} text-white`}
                        >
                          {booking.status}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {booking.status === 'pending' && booking.payment_status === 'pending' && (
                        <>
                          <Button 
                            size="sm"
                            onClick={() => window.location.href = `/payment/${booking.id}`}
                          >
                            💳 Pay Now
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          >
                            ❌ Cancel
                          </Button>
                        </>
                      )}
                      
                      {(booking.status === 'confirmed' || booking.status === 'completed') && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">
                              💬 Chat with {booking.specialist_name}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-lg">
                            <DialogHeader>
                              <DialogTitle>
                                Chat - {booking.service_category} on {booking.date}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="text-center py-8 text-gray-500">
                              Chat system coming soon...
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            👁️ View Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Booking Details</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <label className="font-semibold text-gray-700">Specialist:</label>
                                <p>{booking.specialist_name}</p>
                              </div>
                              <div>
                                <label className="font-semibold text-gray-700">Service:</label>
                                <p className="capitalize">{booking.service_category}</p>
                              </div>
                              <div>
                                <label className="font-semibold text-gray-700">Date & Time:</label>
                                <p>{booking.date} at {booking.time_slot}</p>
                              </div>
                              <div>
                                <label className="font-semibold text-gray-700">Duration:</label>
                                <p>{booking.duration_hours} hours</p>
                              </div>
                              <div className="col-span-2">
                                <label className="font-semibold text-gray-700">Address:</label>
                                <p>{booking.address}</p>
                              </div>
                              {booking.special_instructions && (
                                <div className="col-span-2">
                                  <label className="font-semibold text-gray-700">Instructions:</label>
                                  <p>{booking.special_instructions}</p>
                                </div>
                              )}
                              <div>
                                <label className="font-semibold text-gray-700">Status:</label>
                                <Badge className="ml-2">{booking.status}</Badge>
                              </div>
                              <div>
                                <label className="font-semibold text-gray-700">Total:</label>
                                <p className="font-semibold text-green-600">€{booking.total_amount}</p>
                              </div>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={prevMonth}>
                    ← Prev
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())}>
                    Today
                  </Button>
                  <Button variant="outline" size="sm" onClick={nextMonth}>
                    Next →
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600">
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays().map((date, index) => {
                  const dayBookings = getBookingsForDate(date);
                  const isCurrentMonthDay = isCurrentMonth(date);
                  const isTodayDate = isToday(date);
                  
                  return (
                    <div 
                      key={index}
                      className={`
                        min-h-[80px] p-1 border border-gray-200 
                        ${isCurrentMonthDay ? 'bg-white' : 'bg-gray-50'} 
                        ${isTodayDate ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                        hover:bg-gray-100 transition-colors
                      `}
                    >
                      <div className={`text-sm font-medium mb-1 ${isCurrentMonthDay ? 'text-gray-900' : 'text-gray-400'}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayBookings.slice(0, 2).map(booking => (
                          <div 
                            key={booking.id}
                            className={`text-xs p-1 rounded text-white truncate ${getStatusColor(booking.status)}`}
                            title={`${booking.time_slot} - ${booking.specialist_name} (${booking.service_category})`}
                          >
                            {booking.time_slot} {booking.service_category}
                          </div>
                        ))}
                        {dayBookings.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{dayBookings.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};